import { Divider } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import React, { FC, useCallback, useState } from 'react';

import { DEFAULT_CURRENCY_CODE } from '../../../../constants/locations';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { DrawByProductId } from '../../../../features/draws/types';
import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import { useUrlHasParam } from '../../../../hooks/useUrlHasParam';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { AnalyticsFlowProvider } from '../../../../utils/analytics/flow';
import { renderShopifyArtistName } from '../../../../utils/artists';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { SignUpButton } from '../SignUpButton';
import { toClientUTCDateFormat } from '../utils';
import { PreOrderHeader } from './PreOrderHeader';

interface Props {
  draw: DrawByProductId;
  product: ProductType;
}

export const PreOrderFlow: FC<Props> = ({ draw, product }) => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const preAuthController = usePreAuthDrawCtx();
  const purchasingController = usePurchase();
  const hasCheckoutParam = useUrlHasParam('checkout_id');

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'pre-order',
    });
  }, [, emitter]);

  const launchDate = new Date(toClientUTCDateFormat(product.launchDate));

  return (
    <>
      <div className="flex w-full flex-col space-y-6">
        <PreOrderHeader
          active
          className="w-full"
          description={t('preOrder.isOpen.description', {
            count: draw.allocation,
          })}
          title={t('preOrder.isOpen.title')}
        />
        <div className="flex w-full flex-col items-center space-y-3">
          <PreAuthDrawButton
            buttonLocation="mainCta"
            draw={draw}
            hasCheckoutParam={hasCheckoutParam}
            product={product}
          />
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              handleHowToClick();
            }}
            type="button"
          >
            {t('subscriptionCta.preOrder.learnMoreDialog.title')}
          </button>
        </div>

        <Divider variant="dashed" />

        <div className="flex flex-col text-center">
          <p className="font-bold">
            {isInNextSixDays(launchDate)
              ? t('subscriptionDate.default.countdown')
              : t('subscriptionDate.default.date')}
          </p>
          <p>
            <ProductDate date={launchDate} />
          </p>
        </div>

        <p className="text-center text-neutral-4">
          {t('preOrder.isOpen.titleAllocation', {
            count: draw.allocation,
          })}
        </p>

        <SignUpButton
          isPreOrder
          labels={{ normal: t('notifyMeAtLaunch'), success: t('subscribed') }}
          product={product}
          productRef={getProductRef(purchasingController)}
          productStatus={getProductStatus(product, purchasingController)}
          showIcon
          variant="secondary"
        />
      </div>

      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.preOrder.learnMoreDialog.title')}
      >
        <p>
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph1">
            When we produce a limited edition, we set aside a number for
            collectors to apply for pre-order ahead of the release day. You can
            read more on why we do this in&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              our FAQs
            </a>
            .
          </Trans>
        </p>
        <p className="my-3">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph2')}
        </p>
        <p className="my-3">
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph3">
            For each release, we reserve an allocation for pre-orders for
            different groups of collectors: some for first-time collectors, some
            for existing collectors, and so on. These groups are outlined in
            more detail on&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              this page
            </a>
            . Within these groups, we try to assign pre-orders as fairly as
            possible, giving priority to those who have missed out on editions
            they've applied for in the past, and those who apply early.
          </Trans>
        </p>
        <p className="mb-6">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph4')}
        </p>
        {showDialog && (
          <PreAuthDrawButton
            buttonLocation="dialogCta"
            draw={draw}
            onClick={() => setShowDialog(false)}
            product={product}
          />
        )}
      </SimpleDialog>
      <AnalyticsFlowProvider
        name="pre-order"
        properties={{
          'product title': product.title,
          'artist name': renderShopifyArtistName(
            purchasingController.product?.artist ?? '',
          ),
          'product price':
            product.pricesAndAvailability.pricesAndCurrencies.find(
              (price) => price.currency === DEFAULT_CURRENCY_CODE,
            )?.price || 'unknown',
          'product can be framed': Boolean(
            purchasingController.frameOptions?.length,
          ),
          'product frame upgrade available': Boolean(
            purchasingController.frameOptions,
          ),
          'product frame chosen': Boolean(purchasingController.frame),
          ecommerce: {
            currency: purchasingController.product?.currency ?? '',
            transaction_id: preAuthController.entryIntentResponse.intent?.id,
            value:
              product.pricesAndAvailability.pricesAndCurrencies.find(
                (price) => price.currency === DEFAULT_CURRENCY_CODE,
              )?.price || 'unknown',
            items: [
              {
                item_id: product.id[0].id,
                item_name: product.title,
                item_brand: renderShopifyArtistName(
                  purchasingController.product?.artist ?? '',
                ),
                item_category: product.medium?.join(','),
                price:
                  product.pricesAndAvailability.pricesAndCurrencies.find(
                    (price) => price.currency === DEFAULT_CURRENCY_CODE,
                  )?.price || 'unknown',
                quantity: purchasingController.quantity,
              },
            ],
          },
        }}
      >
        {() =>
          preAuthController.setupSecret && (
            <DialogFlowWithStripe
              clientSecret={preAuthController.setupSecret}
            />
          )
        }
      </AnalyticsFlowProvider>
    </>
  );
};
